import { useLayoutEffect, useState } from "react";
import { NoColorSpace, RepeatWrapping, SRGBColorSpace, Texture } from "three";
import { floorTextureOffset, floorTextureRepeat } from "./studioFloorTiling";

export interface StudioFloorTextures {
  readonly map: Texture;
  readonly normalMap: Texture;
  readonly arm: Texture;
}

/**
 * Configure immutable image/sampler properties once, after React commits and
 * before drei's passive initTexture effect. UVs never belong to this cache.
 */
export function configureStudioFloorTextures(cached: StudioFloorTextures) {
  for (const [texture, colorSpace] of [
    [cached.map, SRGBColorSpace],
    [cached.normalMap, NoColorSpace],
    [cached.arm, NoColorSpace],
  ] as const) {
    const changed =
      texture.wrapS !== RepeatWrapping ||
      texture.wrapT !== RepeatWrapping ||
      texture.anisotropy !== 16 ||
      texture.colorSpace !== colorSpace;
    texture.wrapT = RepeatWrapping;
    texture.wrapS = RepeatWrapping;
    texture.anisotropy = 16;
    texture.colorSpace = colorSpace;
    if (changed) texture.needsUpdate = true;
  }
  // The packed AO, roughness and metalness all use the first UV channel.
  cached.arm.channel = 0;
}

/**
 * A UV-only view of an immutable image. Three's Texture.copy()/clone() calls
 * needsUpdate, which normally increments the SHARED Source.version as well
 * as the texture version. That would re-upload the same JPEG on every mount.
 * These variants only need their own version incremented for first binding;
 * WebGLTextures can reuse the cached Source + identical sampler allocation.
 * Never use this class for mutable image data; image updates belong to the
 * loader-owned texture, whose normal needsUpdate semantics are unchanged.
 */
class StudioFloorUvTexture extends Texture {
  override set needsUpdate(value: boolean) {
    // `value === true`, exactly as three's own setter tests it, so the only
    // difference from the base remains the dropped `source.needsUpdate`.
    if (value === true) this.version++;
  }
}

export interface OwnedStudioFloorTextures extends StudioFloorTextures {
  readonly cached: StudioFloorTextures;
  readonly disposed: boolean;
  setRadius(radius: number): void;
  dispose(): void;
}

/** Call from a commit effect, never a render/useMemo that may be abandoned. */
export function acquireStudioFloorTextures(
  cached: StudioFloorTextures,
): OwnedStudioFloorTextures {
  configureStudioFloorTextures(cached);
  const map = new StudioFloorUvTexture().copy(cached.map);
  const normalMap = new StudioFloorUvTexture().copy(cached.normalMap);
  const arm = new StudioFloorUvTexture().copy(cached.arm);
  const variants = [map, normalMap, arm];
  let disposed = false;
  // The radius these variants currently carry. NaN until the first call, so
  // that one always applies. Held here, against the variants' own state,
  // rather than read back off the mesh: the two do not always reset together
  // (see `useTrackedFloorRadius`), and the frame callback calls this
  // unconditionally on every frame.
  let appliedRadius = Number.NaN;

  return {
    cached,
    map,
    normalMap,
    arm,
    get disposed() {
      return disposed;
    },
    setRadius(radius) {
      if (disposed || radius === appliedRadius) return;
      appliedRadius = radius;
      const repeat = floorTextureRepeat(radius);
      // Derived from the repeat, so the guard above covers both.
      const offset = floorTextureOffset(repeat);
      for (const texture of variants) {
        texture.repeat.set(repeat, repeat);
        // Without this the pattern slides as the patch grows; see
        // floorTextureOffset.
        texture.offset.set(offset, offset);
        texture.updateMatrix();
        // UV transforms are uniforms: neither Texture nor Source is dirty.
      }
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const texture of variants) texture.dispose();
      // Never dispose or clear drei's loader-owned cached textures/Source.
    },
  };
}

/**
 * `radius` seeds the variants as soon as they are acquired.
 *
 * The interactive floor overwrites it from a `useFrame` callback before the
 * first draw, so there the seed is not what you see. It is still the hook's
 * contract rather than dead weight: it is what a consumer that renders without
 * running the frame loop gets, and the only state the variants have before any
 * callback has run. Removing it put `studioFloorTextureLifecycle` in lt-cases
 * on the wrong repeat at mount.
 */
export function useOwnedStudioFloorTextures(
  cached: StudioFloorTextures,
  radius: number,
): OwnedStudioFloorTextures | null {
  const { map, normalMap, arm } = cached;
  const [owned, setOwned] = useState<OwnedStudioFloorTextures | null>(null);

  useLayoutEffect(() => {
    const acquired = acquireStudioFloorTextures({ map, normalMap, arm });
    setOwned(acquired);
    return () => acquired.dispose();
  }, [map, normalMap, arm]);

  // A changed asset base must not render the previous (disposed) variants.
  const current =
    owned &&
    !owned.disposed &&
    owned.cached.map === map &&
    owned.cached.normalMap === normalMap &&
    owned.cached.arm === arm
      ? owned
      : null;

  useLayoutEffect(() => {
    current?.setRadius(radius);
  }, [current, radius]);

  return current;
}
